/**
 * "Give each list its own drop" — rank slots, said plainly (user,
 * 19/10/2026: the first button was "really disgusting … improve that UI and
 * understanding"). A card: what it does in one line, where the group stands
 * (how many of its drops are tied to a rank), the "best N" it covers — set
 * right here when asked (Drop slots) — and one button that says exactly
 * what it will do. #1 always sends to today's best list, #2 to the next…
 * the drops follow the group by themselves every day. Draft only — Save
 * keeps it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useState } from 'react';
import { Check, Hash } from 'lucide-react';

import { Button, HintDot, NumberField, Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DraftGroupRef } from '../../../../drop-generator.types';
import { MINIMAL_FIELD } from '../../general/controls/field-style';
import { rankSlots, rankSlotsPlan } from '../../state/slots/rank-slots';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { ALL_LISTS_CAP } from './list-choice';
import { rankState, tieWords } from '../../state/slots/rank-words';

interface Props {
  link: DraftGroupRef;
  groupName: string;
  editor: ContainerEditor;
  /** The lists the group matches now (null = not known yet). */
  listCount: number | null;
  /** Show the "best N" field (under Drop slots; the group card has its own). */
  withLimit?: boolean;
  /** The group's chip next to the title (under Drop slots, where several groups may show). */
  chip?: ReactNode;
}

/** How many ranks: the "up to", never more than the lists that match now (when any do). */
export function rankCount(listsPerDay: number, listCount: number | null): number {
  if (listsPerDay <= 0) return Math.min(listCount ?? ALL_LISTS_CAP.max, ALL_LISTS_CAP.max); // "every list" (older rows)
  return listCount ? Math.min(listsPerDay, listCount) : listsPerDay;
}

export const RankSlotsCard: FC<Props> = ({ link, groupName, editor, listCount, withLimit = false, chip }) => {
  const [done, setDone] = useState(false);
  const n = rankCount(link.listsPerDay, listCount);
  const plan = rankSlotsPlan(editor.draft.slots, link.listGroupId, n);
  const state = rankState(editor.draft.slots, link.listGroupId, n);
  const tie = () => {
    editor.setField('slots', rankSlots(editor.draft.slots, link.listGroupId, n));
    setDone(true);
  };
  return (
    <div className={cn('flex rounded-md border border-border/70 bg-background', withLimit ? 'gap-3 px-3 py-2.5' : 'items-center gap-2 px-2.5 py-1.5')}>
      <span className={cn('flex shrink-0 items-center justify-center rounded bg-primary/10 text-primary', withLimit ? 'mt-0.5 h-7 w-7' : 'h-5 w-5')} aria-hidden>
        <Hash className={withLimit ? 'h-4 w-4' : 'h-3 w-3'} />
      </span>
      <div className={cn('flex min-w-0 flex-1 gap-x-3 gap-y-1', withLimit ? 'flex-col' : 'flex-wrap items-center justify-between')}>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-xs font-medium">Give each list its own drop {chip}
            {!withLimit ? <HintDot label={`Drop #1 follows today’s best list of ${groupName}, #2 follows the next, then rounds again. Each drop keeps its own time, gateway and amount. ${state.words}. ${tieWords(plan, n)}.`} /> : null}
          </span>
          {withLimit ? <span className="text-xs leading-snug text-muted-foreground">
            Drop #1 always goes to today’s best list of {groupName}, #2 to the next… — they follow the group by themselves every day, each with its own time, gateway and amount.
          </span> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {withLimit ? <LimitField link={link} editor={editor} /> : null}
          <Tooltip content={tieWords(plan, n)}>
            <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={n < 1} onClick={tie} aria-label={`${groupName}: ${tieWords(plan, n)}`}>
              {withLimit ? tieWords(plan, n) : plan.use === 0 ? `Create ${n} drops` : `Assign #1–#${n}${plan.create > 0 ? ` + ${plan.create} new` : ''}`}
            </Button>
          </Tooltip>
          {withLimit || done || state.complete ? <RankStatus state={state} done={done} /> : null}
        </div>
      </div>
    </div>
  );
};

const RankStatus: FC<{ state: ReturnType<typeof rankState>; done: boolean }> = ({ state, done }) => (
  <span className={cn('flex items-center gap-1 text-[11px]', state.complete ? 'text-success' : 'text-muted-foreground')}>
    {state.complete ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
    {state.words}
    {done ? ' · Save to keep it' : ''}
  </span>
);

/** "Best [30] lists" — the group's "up to", right where the drops are tied (user: "update the number of best from here"). */
const LimitField: FC<{ link: DraftGroupRef; editor: ContainerEditor }> = ({ link, editor }) => (
  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
    Best
    <NumberField
      value={link.listsPerDay > 0 ? link.listsPerDay : ALL_LISTS_CAP.default}
      onChange={(v) => editor.groupPatch(link.listGroupId, { listsPerDay: Math.max(ALL_LISTS_CAP.min, Math.min(ALL_LISTS_CAP.max, Math.floor(v))) })}
      min={ALL_LISTS_CAP.min}
      max={ALL_LISTS_CAP.max}
      aria-label="How many of the best lists"
      className={cn(MINIMAL_FIELD, 'h-7 w-14 text-center text-sm')}
    />
    lists
  </span>
);
